import { QueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/services/productService';
import { logDebug, logError } from '@/utils/errorHandler';

/**
 * Invalidates all product-related queries
 * @param queryClient The React Query client
 * @param productId The product ID
 */
export const invalidateProductQueries = async (
  queryClient: QueryClient,
  productId: number | string
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRODUCTS] }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(productId) }),
  ]);
};

/**
 * Invalidates product assets queries
 * @param queryClient The React Query client
 * @param productId The product ID
 */
export const invalidateProductAssets = async (
  queryClient: QueryClient,
  productId: number | string
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.PRODUCT_ASSETS(productId),
  });
};

/**
 * Invalidates localStorage cache for product assets
 * @param productId The product ID
 */
export const invalidateProductAssetsCache = (productId: number | string): void => {
  try {
    localStorage.removeItem(`product_assets_${productId}`);
    logDebug('Product assets cache cleared from localStorage', {
      component: 'queryInvalidation',
      action: 'invalidateProductAssetsCache',
      productId,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : 'Failed to clear localStorage cache',
      {
        component: 'queryInvalidation',
        action: 'invalidateProductAssetsCache',
        metadata: {
          productId,
        },
      },
      {
        showToast: false, // Silent error - cache invalidation failure shouldn't interrupt user
        captureInSentry: true,
        userFacing: false,
      }
    );
  }
};

/**
 * Invalidates both product data and assets
 * @param queryClient The React Query client
 * @param productId The product ID
 */
export const invalidateProductAndAssets = async (
  queryClient: QueryClient,
  productId: number | string
): Promise<void> => {
  await Promise.all([
    invalidateProductQueries(queryClient, productId),
    invalidateProductAssets(queryClient, productId),
    invalidateProductPrices(queryClient, productId),
  ]);

  // Also invalidate localStorage cache
  invalidateProductAssetsCache(productId);
};

/**
 * Invalidates product prices queries
 * @param queryClient The React Query client
 * @param productId The product ID
 */
export const invalidateProductPrices = async (
  queryClient: QueryClient,
  productId: number | string
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.PRODUCT_PRICES(productId),
  });
};
